import { useMemo } from "react";

import { useAtom } from "jotai";
import {
  ChartArea,
  FileChartColumnIncreasing,
  Megaphone,
  PiggyBank,
  Send,
  Settings,
} from "lucide-react";

import { Link, routes, useLocation } from "@redwoodjs/router";

import FeedbackDialog, {
  feedbackDialogAtom,
} from "src/components/FeedbackDialog/FeedbackDialog";
import Logo from "src/components/icon/Logo";
import { settingsDialogAtom } from "src/components/SettingsDialog/SettingsDialog";
import { NavMain } from "src/components/Sidebar/NavMain";
import { NavSecondary } from "src/components/Sidebar/NavSecondary";
import { NavShortcuts } from "src/components/Sidebar/NavShortcuts";
import { StoreSwitcher } from "src/components/Sidebar/StoreSwitcher";
import { TrialCountdown } from "src/components/Sidebar/TrialCountdown";
import { UserNav } from "src/components/Sidebar/UserNav";
import SupportDialog from "src/components/SupportDialog/SupportDialog";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  useSidebar,
} from "src/components/ui/sidebar";
import {
  analyticsPages,
  financePages,
  marketingPages,
} from "src/lib/constants";
import { useFrequentlyUsedSidebarItems } from "src/lib/hooks/useFrequentlyUsedSidebar";

const getItems = (pages, router, pathname) => {
  return Object.values(pages).map((item) => {
    const url = router({ child: item.id }).split("?")[0];
    return {
      title: item.name,
      url,
      isActive: pathname.startsWith(url),
    };
  });
};

const Footer = () => {
  const { state } = useSidebar();

  return (
    <>
      <UserNav />

      <div className="h-10 px-2 sm:px-0">
        {state !== "collapsed" ? (
          <div className="text-muted-foreground flex w-full flex-col flex-nowrap overflow-hidden text-xs leading-tight text-balance">
            <p className="whitespace-nowrap">
              &copy; {new Date().getFullYear()} {process.env.APP_NAME}
            </p>
            <div className="whitespace-nowrap">
              <Link
                to={routes.privacyPolicy()}
                className="underline underline-offset-4"
              >
                Privacy Policy
              </Link>
              &nbsp;&middot;&nbsp;
              <Link to={routes.eula()} className="underline underline-offset-4">
                Terms of Service
              </Link>
            </div>
          </div>
        ) : (
          <Logo className="size-8" noText />
        )}
      </div>
    </>
  );
};

export function AppSidebar({ ...props }) {
  const { pathname } = useLocation();
  // const [supportDialogState, setSupportDialogState] =
  //   useAtom(supportDialogAtom);
  const [feedbackDialogState, setFeedbackDialogState] =
    useAtom(feedbackDialogAtom);
  const [, setSettingsDialogState] = useAtom(settingsDialogAtom);
  const shortcuts = useFrequentlyUsedSidebarItems();

  const data = useMemo(
    () => ({
      navMain: [
        {
          title: "Business Intelligence",
          icon: ChartArea,
          isActive: pathname.startsWith("/analytics"),
          items: getItems(analyticsPages, routes.analytics, pathname),
        },
        {
          title: "Finance",
          icon: PiggyBank,
          isActive: pathname.startsWith("/finance"),
          items: getItems(financePages, routes.finance, pathname),
        },
        {
          title: "Marketing",
          icon: Megaphone,
          isActive: pathname.startsWith("/marketing"),
          items: getItems(marketingPages, routes.marketing, pathname),
        },
      ],
      secondary: [
        {
          title: "Settings",
          url: "#",
          icon: Settings,
          onClick: () => {
            setSettingsDialogState((prev) => ({
              ...prev,
              open: true,
            }));
          },
        },
        // {
        //   title: "Support",
        //   url: "#",
        //   icon: LifeBuoy,
        //   onClick: () => {
        //     setSupportDialogState({
        //       ...supportDialogState,
        //       open: true,
        //     });
        //   },
        // },
        {
          title: "Feedback",
          url: "#",
          icon: Send,
          onClick: () => {
            setFeedbackDialogState({
              ...feedbackDialogState,
              open: true,
            });
          },
        },
      ].filter(Boolean),
    }),
    [
      pathname,
      feedbackDialogState,
      setFeedbackDialogState,
      setSettingsDialogState,
    ]
  );

  return (
    <Sidebar collapsible="icon" variant="inset" {...props}>
      <SidebarHeader>
        <StoreSwitcher teams={data.teams} />
      </SidebarHeader>
      <SidebarContent>
        <NavMain
          dashboardItem={{
            title: "Insights",
            icon: FileChartColumnIncreasing,
            url: routes.dashboard(),
            isActive: pathname === "/dashboard",
          }}
          items={data.navMain}
        />
        <NavShortcuts items={shortcuts} />

        {/*<SidebarCta />*/}

        <div className="mt-auto space-y-2">
          <TrialCountdown />
          <NavSecondary items={data.secondary} />
        </div>
      </SidebarContent>
      <SidebarFooter>
        <Footer />
      </SidebarFooter>
      <SupportDialog />
      <FeedbackDialog />
    </Sidebar>
  );
}
